/* eslint-disable import/prefer-default-export */

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { errorRedux } from 'interfaces/redux';
import { AxiosError } from 'axios';
import {
  customerPaymentResponse,
  payloadStripeDataMethodPayment,
  responseCreatePaymentIntent,
  responseCreateSetupIntent,
  responseEcommerceLinkGeneration,
  responseEcommerceLinkGenerationByProductCodeAndBillingCode,
} from 'interfaces/Ecommerce';
import {
  getLinkGeneration,
  getLinkGenerationByProductAndBillingCode,
  postCreateCustomer,
  postCreatePaymentIntent,
  postCreateSetupIntent,
  postStripeDataPaymentMethod,
} from './service';

export const createSetupIntent = createAsyncThunk<responseCreateSetupIntent, string, { rejectValue: errorRedux }>(
  'ecommerce/create/setup/intent',
  async (cpf, { rejectWithValue }) => {
    try {
      const response = await postCreateSetupIntent(cpf);
      return response;
    } catch (error) {
      const err = error as AxiosError<errorRedux>;
      return rejectWithValue(err.response?.data as errorRedux);
    }
  },
);

export const createPaymentIntent = createAsyncThunk<
  responseCreatePaymentIntent,
  undefined,
  { rejectValue: errorRedux }
>('ecommerce/create/payment/intent', async (_, { rejectWithValue }) => {
  try {
    const data = await postCreatePaymentIntent();
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

export const createCustomerPayment = createAsyncThunk<customerPaymentResponse, string, { rejectValue: errorRedux }>(
  'ecommerce/create/customer',
  async (cpf, { rejectWithValue }) => {
    try {
      const response = await postCreateCustomer(cpf);
      return response;
    } catch (error) {
      const err = error as AxiosError<errorRedux>;
      return rejectWithValue(err.response?.data as errorRedux);
    }
  },
);

export const stripeDataPaymentMethod = createAsyncThunk<void, payloadStripeDataMethodPayment>(
  'ecommerce/stripeDataPaymentMethod',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postStripeDataPaymentMethod(payload);
      return response;
    } catch (error) {
      const err = error as AxiosError<errorRedux>;
      return rejectWithValue(err.response?.data as errorRedux);
    }
  },
);

export const linkGenerationByProductAndBillingCode = createAsyncThunk<
  responseEcommerceLinkGenerationByProductCodeAndBillingCode,
  { productCode: number; billingCode: number },
  { rejectValue: errorRedux }
>('ecommerce/linkGenerationByProductAndBillingCode', async ({ productCode, billingCode }, { rejectWithValue }) => {
  try {
    const response = await getLinkGenerationByProductAndBillingCode(productCode, billingCode);
    return response;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

export const linkGeneration = createAsyncThunk<responseEcommerceLinkGeneration, void, { rejectValue: errorRedux }>(
  'ecommerce/linkGeneration',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getLinkGeneration();
      return response;
    } catch (error) {
      const err = error as AxiosError<errorRedux>;
      return rejectWithValue(err.response?.data as errorRedux);
    }
  },
);

interface IPayment {
  cpf: string;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  isReady: boolean;
  isLoadingCustomer: boolean;
  message: string;
  methodPayment: string | undefined;
}

const initialState: IPayment = {
  cpf: '',
  isLoading: false,
  isSuccess: false,
  isError: false,
  isReady: false,
  isLoadingCustomer: false,
  message: '',
  methodPayment: undefined,
};
const ecommerceSlice = createSlice({
  name: 'ecommerce',
  initialState,
  reducers: {
    clearState: () => {
      return initialState;
    },
    setMethodPayment: (state, action: PayloadAction<string | undefined>) => {
      return { ...state, methodPayment: action.payload };
    },
    setIsLoadingCustomer: (state, action: PayloadAction<boolean>) => {
      return { ...state, isLoadingCustomer: action.payload };
    },
    setCpf: (state, action: PayloadAction<string>) => {
      return { ...state, cpf: action.payload };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      return { ...state, isLoading: action.payload };
    },
    setSuccess: (state, action: PayloadAction<boolean>) => {
      return { ...state, isSuccess: action.payload };
    },
    setError: (state, action: PayloadAction<boolean>) => {
      return { ...state, isError: action.payload };
    },
    setMessage: (state, action: PayloadAction<string>) => {
      return { ...state, message: action.payload };
    },
    setIsReady: (state, action: PayloadAction<boolean>) => {
      return { ...state, isReady: action.payload };
    },
  },
});

export const {
  setLoading,
  setSuccess,
  setError,
  setMessage,
  clearState,
  setIsReady,
  setCpf,
  setIsLoadingCustomer,
  setMethodPayment,
} = ecommerceSlice.actions;
export default ecommerceSlice.reducer;
